import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { fetchCallDetail } from "./supervision-api";
import { DEMO_SUPERVISED_CALLS } from "../../lib/demo-data";
import { getManualFlag } from "./lib/flags-store";
import type {
  Alert,
  RecentMessage,
  SupervisedCall,
  SupervisedCallDetail,
  TranscriptMessage,
} from "./types";

/** Comfortably inside the server's 60s watch lease, so it never lapses while open. */
const DETAIL_REFRESH_MS = 20_000;

/**
 * Fetched history plus the live tail, deduped by `item_id`.
 *
 * Order matters and is not symmetric. The roster preview is clipped server-side to keep
 * twenty-five tiles small, so a preview line must only ever *fill a gap* -- never replace a
 * message we already hold in full, or the newest lines of the transcript would render
 * truncated while the complete text sat unused in the same hook.
 */
export function mergeTranscript(
  history: TranscriptMessage[] | undefined,
  preview: RecentMessage[] | undefined,
): TranscriptMessage[] {
  const byId = new Map<string, TranscriptMessage>();
  let synthetic = 0;

  for (const message of history ?? []) {
    // item_id is the stable handle; fall back to a positional key only when it is absent
    // (older calls), which at worst duplicates a line rather than dropping one.
    byId.set(message.item_id || `#${synthetic++}`, message);
  }

  for (const line of preview ?? []) {
    if (line.item_id && byId.has(line.item_id)) continue;
    byId.set(line.item_id || `#${synthetic++}`, {
      role: line.role,
      text: line.text,
      created_at: line.created_at,
      item_id: line.item_id,
      interrupted: line.interrupted,
      held: line.held ?? undefined,
      // A turn's cut arrives on the roster preview before the drawer's own
      // detail fetch catches up, which is exactly when it matters most -- this is
      // the live path. Dropping these would hide the cut until a refetch.
      ...speechSplitOf(line),
    });
  }

  return Array.from(byId.values());
}

/** The spoken/unspoken fields of a message, omitting the ones it does not carry. */
function speechSplitOf(message: RecentMessage | TranscriptMessage): Partial<TranscriptMessage> {
  const split: Partial<TranscriptMessage> = {};
  if (message.spoken_text !== undefined) split.spoken_text = message.spoken_text;
  if (message.spoken_ratio !== undefined) split.spoken_ratio = message.spoken_ratio;
  if (message.spoken_measure !== undefined) split.spoken_measure = message.spoken_measure;
  if (message.speech_ms !== undefined) split.speech_ms = message.speech_ms;
  if (message.backlog_ms !== undefined) split.backlog_ms = message.backlog_ms;
  if (message.spoken_truncated !== undefined) split.spoken_truncated = message.spoken_truncated;
  // `unspoken_text` is absent from a preview by design (the server drops it to
  // protect the roster payload), so a preview-only turn shows the spoken share
  // without the struck-through tail until the detail read lands.
  if ("unspoken_text" in message && message.unspoken_text !== undefined) {
    split.unspoken_text = message.unspoken_text;
  }
  if (message.spoken_chars !== undefined) split.spoken_chars = message.spoken_chars;
  if (message.unspoken_chars !== undefined) split.unspoken_chars = message.unspoken_chars;
  if (message.planned_chars !== undefined) split.planned_chars = message.planned_chars;
  return split;
}

/**
 * The focused call: full transcript and full alert list, fetched on demand.
 *
 * The roster only carries a three-line preview now, so the drawer needs its own read. It
 * merges that history with the live tail from the stream by `item_id`, the same way the
 * roster tile dedupes -- a refetch on every incoming turn would be both slow and visibly
 * jumpy, and the stream alone has no history to show.
 */
export function useCallDetail(callId: string | null, liveCall: SupervisedCall | null) {
  const [detail, setDetail] = useState<SupervisedCallDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestRef = useRef(0);
  const liveCallRef = useRef(liveCall);
  liveCallRef.current = liveCall;
  const pendingSince = useRef<number | null>(null);
  const denyAccess = useCallback((caught: unknown) => {
    const status = (caught as {status?: number})?.status;
    if (status === 401 || status === 403) {
      setDetail(null);
      window.dispatchEvent(new Event("supervision-access-denied"));
    }
  }, []);
  const mergeDetail = useCallback((next: SupervisedCallDetail) => {
    setDetail(previous => {
      if (!previous || previous.call_id !== next.call_id) return next;
      const hasFinalTranscript = next.transcript_source === "summary" &&
        (next.transcript_availability === "available" || next.transcript_availability === "empty");
      return {...previous, ...next, ended_at: next.ended_at ?? previous.ended_at, messages: hasFinalTranscript ? next.messages :
        mergeTranscript(previous.messages, next.messages)};
    });
  }, []);
  const inFlightRef = useRef(false);

  const load = useCallback(async () => {
    if (!callId) {
      setDetail(null);
      return;
    }
    const request = ++requestRef.current;
    // Drop the previous call's transcript and alerts before fetching, or the drawer shows
    // the new caller's name over the old caller's conversation for as long as the request
    // takes -- and the alert panel would act on the wrong call's alerts.
    setDetail(null);
    pendingSince.current = null;
    setError(null);
    setLoading(true);

    // DEMO MODE: load synthetic detail directly without hitting the backend API.
    if (liveCallRef.current?.is_demo || callId.startsWith("live_sup_")) {
      const demo = liveCallRef.current ?? DEMO_SUPERVISED_CALLS.find(c => c.call_id === callId);
      if (demo) {
        setDetail({
          ...demo,
          customer_phone: demo.customer_phone || "+91 98765 43210",
          lifecycle: "live",
          flag: getManualFlag(callId) ?? demo.flag ?? null,
          messages: (demo.recent ?? []).map((m, i) => ({
            role: m.role,
            text: m.text,
            created_at: m.created_at,
            item_id: ("item_id" in m && typeof m.item_id === "string" ? m.item_id : undefined) || `turn-${i}`,
          })),
          alerts: demo.alerts?.top ? [demo.alerts.top] : [],
          transcript_availability: "available",
          transcript_source: "live",
        });
        setError(null);
        setLoading(false);
        return;
      }
    }

    try {
      const response = await fetchCallDetail(callId);
      // A slower earlier request must not overwrite a newer one; supervisors click fast.
      if (requestRef.current !== request) return;
      const callData = response.call;
      if (!callData.flag) {
        const manual = getManualFlag(callId);
        if (manual) callData.flag = manual;
      }
      mergeDetail(callData);
      setError(null);
    } catch (caught) {
      if (requestRef.current !== request) return;
      denyAccess(caught);
      setError((caught as Error)?.message || "Could not load this call.");
    } finally {
      if (requestRef.current === request) setLoading(false);
    }
  }, [callId, mergeDetail, denyAccess]);

  const invalidate = useCallback(() => { ++requestRef.current; inFlightRef.current = false; }, []);
  useEffect(() => {
    void load();
    return invalidate;
  }, [load, invalidate]);

  /**
   * Refetch without blanking the panel.
   *
   * `load` clears the drawer first so a call switch cannot show one caller's name over
   * another's transcript. That reset is wrong for a poll, which must leave what is on
   * screen alone.
   */
  const refreshQuietly = useCallback(async () => {
    // Its own in-flight guard, because `loading` is not one: that flag is set by `load`
    // alone, so the catch-up effect below -- which re-runs on every SSE tick, and whose
    // condition stays true until a response lands -- would otherwise stack overlapping
    // requests against Core and invalidate each through requestRef.
    if (!callId || inFlightRef.current || liveCallRef.current?.is_demo || callId.startsWith("live_sup_")) return;
    inFlightRef.current = true;
    const request = ++requestRef.current;
    try {
      const response = await fetchCallDetail(callId);
      if (requestRef.current !== request) return;
      mergeDetail(response.call);
      setError(null);
    } catch (caught) {
      if (requestRef.current === request) { denyAccess(caught); setError((caught as Error).message); }
      // A failed background refresh leaves the last good detail on screen. The operator
      // already has a transcript; replacing it with an error would be a downgrade.
    } finally {
      inFlightRef.current = false;
    }
  }, [callId, mergeDetail, denyAccess]);

  const lifecycle = detail?.lifecycle === "ended_complete" ? "ended_complete"
    : liveCall?.lifecycle?.startsWith("ended") ? liveCall.lifecycle : detail?.lifecycle ?? liveCall?.lifecycle;
  useEffect(() => {
    if (!callId || lifecycle === "ended_complete") return;
    const pending = lifecycle === "ended_pending" || lifecycle === "unavailable";
    if (pending && pendingSince.current === null) pendingSince.current = Date.now();
    const timer = setInterval(() => {
      if (pending && pendingSince.current !== null && Date.now() - pendingSince.current >= 120000) return;
      void refreshQuietly();
    }, DETAIL_REFRESH_MS);
    return () => clearInterval(timer);
  }, [callId, lifecycle, refreshQuietly]);

  useEffect(() => {
    if (liveCall?.lifecycle && liveCall.lifecycle !== "live") void refreshQuietly();
  }, [liveCall?.lifecycle, refreshQuietly]);

  // The belt to the reset's braces: even if a response arrives out of order, detail for a
  // different call is never rendered. Identity is checked, not assumed.
  const detailForCall = detail && detail.call_id === callId ? detail : null;

  // Refetch when the live tile shows turns the fetched window cannot contain. The preview
  // is only three lines, so anything faster than that leaves a gap the merge cannot close.
  //
  // Gated so this cannot become a refetch storm: both counts come from the same Redis
  // field in production and converge, but if they ever disagree persistently the condition
  // stays true, and ungated each response would immediately trigger the next request.
  // `loading` covers a `load` in flight; refreshQuietly carries its own guard, since it
  // deliberately does not touch `loading`.
  const liveCount = liveCall?.message_count ?? 0;
  const detailCount = detailForCall?.message_count ?? 0;
  useEffect(() => {
    if (!callId || !detailForCall || loading) return;
    const recent = liveCall?.recent?.length ?? 0;
    // Quietly: this is a catch-up on the SAME call, so there is nothing to protect against
    // by blanking first. `load` clears the drawer, and on a fast-talking call this
    // condition stays true across responses -- so the transcript, the alerts and the
    // guidance chips all flickered away and back every few seconds.
    if (liveCount - detailCount > recent) void refreshQuietly();
  }, [callId, detailForCall, liveCall, liveCount, detailCount, loading, refreshQuietly]);

  const messages = useMemo<TranscriptMessage[]>(
    () => detailForCall?.transcript_source === "summary" &&
      (detailForCall.transcript_availability === "available" || detailForCall.transcript_availability === "empty")
      ? detailForCall.messages ?? [] : mergeTranscript(detailForCall?.messages, liveCall?.recent),
    [detailForCall?.messages, detailForCall?.transcript_source, detailForCall?.transcript_availability, liveCall?.recent],
  );

  const alerts = useMemo<Alert[]>(() => detailForCall?.alerts ?? [], [detailForCall?.alerts]);

  /** Patch one alert in place after a triage action, instead of refetching the call. */
  const applyAlert = useCallback((updated: Alert) => {
    setDetail((previous) => {
      if (!previous) return previous;
      return {
        ...previous,
        alerts: (previous.alerts ?? []).map((alert) =>
          alert.alert_id === updated.alert_id ? updated : alert,
        ),
      };
    });
  }, []);

  return { detail: detailForCall, messages, alerts, loading, error, reload: refreshQuietly, applyAlert };
}
